// PDF Viewer Controller - Adds close functionality to browser's native PDF viewer
import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['overlay', 'closeButton', 'backButton', 'info']

  connect() {
    // Check if we're viewing a PDF in the browser's native viewer
    this.checkIfPdfViewer()
    
    // Add event listeners
    this.addEventListeners()
    
    // Hide overlay if we're in modal system
    if (this.isInModalSystem()) {
      this.hideOverlay()
    }
  }

  disconnect() {
    this.removeEventListeners()
  }

  checkIfPdfViewer() {
    // Check if the current page is displaying a PDF
    const isPdfViewer = this.isPdfViewerPage()
    
    if (isPdfViewer) {
      this.showOverlay()
    } else {
      this.hideOverlay()
    }
  }

  isPdfViewerPage() {
    // Don't activate if we're in our modal system
    if (this.isInModalSystem()) {
      return false
    }
    
    // Check various indicators that we're in a PDF viewer
    const url = window.location.href
    const contentType = document.contentType || ''
    const hasPdfEmbed = document.querySelector('embed[type="application/pdf"]')
    const hasPdfObject = document.querySelector('object[type="application/pdf"]')
    const hasPdfIframe = document.querySelector('iframe[src*=".pdf"]')
    
    return (
      url.includes('.pdf') ||
      contentType.includes('application/pdf') ||
      hasPdfEmbed ||
      hasPdfObject ||
      hasPdfIframe ||
      this.isBrowserPdfViewer()
    )
  }

  isInModalSystem() {
    // Check if we're in our modal system by looking for our modal containers
    const hasModalContainer = document.querySelector('.file-modal') ||
                             document.querySelector('.pdf-outer-container') ||
                             document.querySelector('.text-file-container')
    
    // Check if any modal is currently displayed
    const visibleModal = document.querySelector('.file-modal[style*="flex"]') ||
                        document.querySelector('.file-modal:not([style*="none"])')
    
    return hasModalContainer || visibleModal
  }

  isBrowserPdfViewer() {
    // Check for browser-specific PDF viewer indicators
    const body = document.body
    const hasPdfViewerClass = body.classList.contains('pdf-viewer') || 
                             body.classList.contains('pdfjs-viewer')
    
    // Check for PDF.js viewer elements
    const hasPdfjsElements = document.querySelector('#viewer, .pdfViewer, .page')
    
    // Check for browser PDF viewer elements
    const hasBrowserPdfElements = document.querySelector('embed[type="application/pdf"]') ||
                                 document.querySelector('object[type="application/pdf"]')
    
    // Check for custom PDF viewer web components
    const hasCustomPdfViewer = document.querySelector('viewer-pdf-sidenav') ||
                              document.querySelector('viewer-thumbnail-bar') ||
                              document.querySelector('#pdf-canvas') ||
                              document.querySelector('#sidenav-container')
    
    return hasPdfViewerClass || hasPdfjsElements || hasBrowserPdfElements || hasCustomPdfViewer
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    } else {
      this.createOverlay()
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }
  }

  createOverlay() {
    // Create the overlay if it doesn't exist
    const overlay = document.createElement('div')
    overlay.className = 'pdf-viewer-overlay'
    overlay.setAttribute('data-pdf-viewer-target', 'overlay')
    
    // Create close button
    const closeButton = document.createElement('button')
    closeButton.className = 'pdf-close-button'
    closeButton.innerHTML = '&times;'
    closeButton.setAttribute('data-pdf-viewer-target', 'closeButton')
    closeButton.setAttribute('data-action', 'click->pdf-viewer#closePdf')
    closeButton.setAttribute('aria-label', 'Close PDF')
    overlay.appendChild(closeButton)
    
    // Create back button
    const backButton = document.createElement('button')
    backButton.className = 'pdf-back-button'
    backButton.innerHTML = '<span class="back-icon">←</span> Back'
    backButton.setAttribute('data-pdf-viewer-target', 'backButton')
    backButton.setAttribute('data-action', 'click->pdf-viewer#goBack')
    backButton.setAttribute('aria-label', 'Go back')
    overlay.appendChild(backButton)
    
    // Create info message
    const info = document.createElement('div')
    info.className = 'pdf-info'
    info.innerHTML = 'Press ESC or click X to close'
    info.setAttribute('data-pdf-viewer-target', 'info')
    overlay.appendChild(info)
    
    // Add to body
    document.body.appendChild(overlay)
  }

  addEventListeners() {
    // Add ESC key listener
    this.escKeyHandler = (event) => {
      if (event.key === 'Escape' && this.isPdfViewerPage()) {
        this.closePdf()
      }
    }
    document.addEventListener('keydown', this.escKeyHandler)
    
    // Add visibility change listener to detect when PDF viewer is opened
    this.visibilityHandler = () => {
      if (!document.hidden) {
        setTimeout(() => this.checkIfPdfViewer(), 100)
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
    
    // Add periodic check to hide overlays when modal system is active
    this.modalCheckInterval = setInterval(() => {
      if (this.isInModalSystem()) {
        this.hideOverlay()
      }
    }, 500)
  }

  removeEventListeners() {
    if (this.escKeyHandler) {
      document.removeEventListener('keydown', this.escKeyHandler)
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    if (this.modalCheckInterval) {
      clearInterval(this.modalCheckInterval)
    }
  }

  closePdf() {
    // Try multiple methods to close the PDF viewer
    this.tryCloseMethods()
  }

  goBack() {
    // Go back to the previous page
    if (window.history.length > 1) {
      window.history.back()
    } else {
      // If no history, try to close the tab/window
      this.closePdf()
    }
  }

  tryCloseMethods() {
    // Method 1: Try to close the current tab/window
    if (window.opener) {
      window.close()
      return
    }
    
    // Method 2: Try to go back in history
    if (window.history.length > 1) {
      window.history.back()
      return
    }
    
    // Method 3: Try to navigate to a known page
    const referrer = document.referrer
    if (referrer && referrer !== window.location.href) {
      window.location.href = referrer
      return
    }
    
    // Method 4: Try to navigate to the root of the application
    const currentUrl = window.location.href
    const baseUrl = currentUrl.split('/').slice(0, 3).join('/')
    window.location.href = baseUrl
    
    // Method 5: Show a message if all else fails
    setTimeout(() => {
      if (window.location.href === currentUrl) {
        alert('Please use your browser\'s back button or close this tab to return to the application.')
      }
    }, 1000)
  }
}
